/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import {
	GridApi,
	GridOptions,
	GridReadyEvent,
	SortChangedEvent,
	SortDirection
} from "ag-grid-community";
import { Subject, takeUntil, tap } from "rxjs";
import { ButtonComponent } from "../../shared/components/button/button.component";
import { DatepickerComponent } from "../../shared/components/datepicker/datepicker.component";
import { DropdownComponent } from "../../shared/components/dropdown/dropdown.component";
import { PopoverFloatingComponent } from "../../shared/components/popover/popover.component";
import { TableActionsComponent } from "../../shared/components/table/cell-renderers/table-actions/table-actions.component";
import { PaginationComponent } from "../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../shared/components/table/table.component";
import { SingleRowAnomalousApplication } from "../../shared/models/http/anomaly/single-row-anomalous-application.model";
import { FilterApplication } from "../../shared/models/http/dashboard/filter-page-response.model";
import { PagedResponse } from "../../shared/models/ui/paged-response.model";
import { SelectOption } from "../../shared/models/ui/select-option.model";
import { TableResponse } from "../../shared/models/ui/table-response.model";
import { TimePeriodRange } from "../../shared/models/ui/time-period-range.model";
import { AnomalyDetectionService } from "../../shared/services/anomaly-detection.service";
import { DashboardSoftwareAssetsService } from "../../shared/services/dashboard-software-assets.service";
import { StorageService } from "../../shared/services/storage.service";
import { PATHS } from "../../shared/utils/constants";
import { getCurrentWeekRange } from "../../shared/utils/reporting-period";
import {
	formatDateForBE,
	formatNumberMaxDecimals,
	getTableIndex,
	roundValueUpTo
} from "../../shared/utils/utils";

@Component({
	selector: "globalictcc-anomalies-detection",
	standalone: true,
	imports: [
		ButtonComponent,
		DatepickerComponent,
		PaginationComponent,
		TableComponent,
		PopoverFloatingComponent,
		DropdownComponent,
		ReactiveFormsModule
	],
	templateUrl: "./anomalies-detection.component.html",
	styleUrl: "./anomalies-detection.component.scss"
})
export class AnomaliesDetectionComponent implements OnInit, OnDestroy {
	public timePeriodFormGroup = new FormGroup({
		timePeriod: new FormControl<TimePeriodRange>(getCurrentWeekRange(), [Validators.required])
	});

	public filterFormGroup = new FormGroup({
		applications: new FormControl<string[]>([], [Validators.required])
	});

	public anomaliesTableHasError: boolean = false;
	public currentPage: number = 0;
	public pageSize: number = 12;
	public totalItems: number = 0;
	public totalPages: number = 1;
	public pageItems: number = 0;
	public gridOptions: GridOptions = {
		columnDefs: [
			{
				headerName: "No.",
				sortable: false,
				valueFormatter: params =>
					getTableIndex(this.currentPage, this.pageSize, params.node?.rowIndex)
			},
			{
				headerName: "Actions",
				sortable: false,
				cellRenderer: TableActionsComponent,
				cellRendererParams: {
					actions: [{ iconName: "view-on", onClick: this.onDetails.bind(this) }]
				}
			},
			{ headerName: "Application", field: "applicationId" },
			{ headerName: "Detection", field: "anomaliesAmount" },
			{
				headerName: "CO2 Emission",
				field: "totalCo2",
				valueFormatter: params =>
					params.value !== undefined
						? formatNumberMaxDecimals(roundValueUpTo(params.value))
						: ""
			}
		],
		rowData: [],
		onGridReady: this._onGridReady.bind(this),
		onSortChanged: this._onSortChanged.bind(this)
	};

	public applicationOptions: SelectOption[] = [];

	private _tenantId: string = "";
	private _gridApi: GridApi;
	private _sortField?: string;
	private _sortDirection?: SortDirection;
	private _destroy$ = new Subject<void>();

	constructor(
		private _router: Router,
		private _anomalyDetectionService: AnomalyDetectionService,
		private _storageService: StorageService,
		private _dashboardSoftwareAssetsService: DashboardSoftwareAssetsService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();

		this._dashboardSoftwareAssetsService
			.getApplicationsIds("", null)
			.pipe(takeUntil(this._destroy$))
			.subscribe(response => {
				this.applicationOptions = response.content.map((app: FilterApplication) => ({
					label: app.name,
					value: app.name
				}));

				this.filterFormGroup.patchValue({
					applications: this.applicationOptions.map(option => option.value)
				});
			});
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Event handler for the grid's 'gridReady' event.
	 *
	 * This method is called when the grid is fully initialized and ready to be interacted with.
	 * It sets the grid API reference, fetches the anomalies data, and adjusts the column sizes to fit the grid's width.
	 *
	 * @param params - The event parameters containing the grid API and other details.
	 */
	private _onGridReady(params: GridReadyEvent) {
		this._gridApi = params.api;

		this.fetchAnomalies();
	}

	/**
	 * Handles the sort changed event for the anomalies detection component.
	 *
	 * This method is triggered when the sorting of columns changes. It retrieves the
	 * column that has a sort applied, updates the internal sort field and direction
	 * properties, and then fetches the anomalies based on the new sorting criteria.
	 *
	 * @param params - The event parameters containing the API and column state information.
	 */
	private _onSortChanged(params: SortChangedEvent) {
		const columnWithSort = params.api.getColumnState().find(col => col.sort !== null);
		this._sortField = columnWithSort?.colId;
		this._sortDirection = columnWithSort?.sort;

		this.fetchAnomalies();
	}

	/**
	 * Navigates to the anomalies detection details page with query parameters.
	 *
	 * @param rowData - The data of the selected row containing the application ID.
	 */
	public onDetails(rowData: SingleRowAnomalousApplication) {
		const { start, end } = this.timePeriodFormGroup.controls.timePeriod.value || {
			start: null,
			end: null
		};

		this._router.navigate([PATHS.ANOMALIES_DETECTION_DETAILS], {
			queryParams: {
				applicationName: rowData.applicationId || "",
				startDate: formatDateForBE(start),
				endDate: formatDateForBE(end)
			}
		});
	}

	/**
	 * Navigates back to the previous page using the environment service.
	 * This method is typically called when the user wants to return to the previous view.
	 */
	public onGoBack() {
		this._router.navigate([PATHS.TOOLBOX]);
	}

	/**
	 * Applies the filter and fetches the anomalies based on the applied filter criteria.
	 * This method is triggered when the filter is applied by the user.
	 */
	public onFilterApply() {
		this.fetchAnomalies();
	}

	/**
	 * Navigates to the previous page of anomalies.
	 * Decrements the current page number and fetches the anomalies for the updated page.
	 */
	public onPreviousPage() {
		this.currentPage--;

		this.fetchAnomalies();
	}

	/**
	 * Advances to the next page of anomalies and fetches the data for that page.
	 * Increments the current page number and triggers the anomalies fetch operation.
	 */
	public onNextPage() {
		this.currentPage++;

		this.fetchAnomalies();
	}

	/**
	 * Fetches anomalies based on the selected time period and applications.
	 *
	 * This method retrieves the start and end dates from the `timePeriodFormGroup` form controls,
	 * and the selected applications from the `filterFormGroup` form controls. If the start date is not
	 * defined, the method returns early.
	 *
	 * It then calls the `getAllAnomalousApplication` method of the `_anomalyDetectionService` with the
	 * necessary parameters including tenant ID, current page, page size, start date, end date, sort field,
	 * selected applications, and sort direction. The response is subscribed to and the grid data is updated
	 * with the fetched anomalies.
	 */
	public fetchAnomalies() {
		const startDate = formatDateForBE(
			this.timePeriodFormGroup.controls.timePeriod.value?.start
		);
		const endDate = formatDateForBE(this.timePeriodFormGroup.controls.timePeriod.value?.end);

		if (!startDate) return;

		this._gridApi.setGridOption("loading", true);

		this._anomalyDetectionService
			.getAllAnomalousApplication(
				this._tenantId,
				this.currentPage,
				this.pageSize,
				startDate,
				endDate,
				this._sortField,
				this.filterFormGroup.controls.applications.value || undefined,
				this._sortDirection || undefined
			)
			.pipe(
				tap({
					next: table => this._updateGridData(table),
					finalize: () => this._gridApi.setGridOption("loading", false)
				})
			)
			.subscribe();
	}

	/**
	 * Updates the grid data with the provided paged anomalies.
	 * @param {TableResponse<PagedResponse<SingleRowAnomalousApplication>>} table - The paged response containing the anomalies data.
	 *
	 * @remarks
	 * This method sets the grid's row data with the contents of the paged anomalies response.
	 * It also updates the pagination information including the number of items per page, total pages, and total items.
	 */
	private _updateGridData(table: TableResponse<PagedResponse<SingleRowAnomalousApplication>>) {
		const { rowData: page, hasError } = table;

		this.anomaliesTableHasError = hasError;

		if (!hasError) {
			this.pageItems = page.contents.length;
			this.totalPages = page.totalPages;
			this.totalItems = page.totalElements;
			this.pageSize = page.size;
		}

		this._gridApi.setGridOption("rowData", hasError ? [] : page.contents);
	}
}
